import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Box,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  MdDownload,
  MdThumbUp,
  MdThumbDown,
  MdReply,
  MdRefresh,
  MdError,
  MdWarning,
  MdAccessTime,
} from "react-icons/md";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const LeadsReports = () => {
  const { id } = useParams();
  const [status, setStatus] = useState("loading"); // loading | expired | invalid | ok | data
  const [data, setData] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentType, setCurrentType] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // For better error handling

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Initial link validity check
  useEffect(() => {
    const checkLink = async () => {
      try {
        const res = await fetch(`${API_ENDPOINT}/api/check-leads-link/${id}`, {
          method: "GET",
        });
        const json = await res.json();
        // Sanitize and validate customerId
        setCustomerId(json.customerId ? String(json.customerId) : "Unknown");
        if (json.type === "expired") {
          setStatus("expired");
          setErrorMessage("This link has expired. Please request a new one.");
        } else if (json.type === "invalid") {
          setStatus("invalid");
          setErrorMessage(
            "Unauthorized access. Please check the link and try again."
          );
        } else if (json.type === "ok") {
          setStatus("ok");
        } else {
          setStatus("invalid");
          setErrorMessage("Unexpected response from server.");
        }
      } catch (err) {
        setStatus("invalid");
        setErrorMessage("Failed to validate link. Please try again later.");
        console.error("Link check error:", err);
      }
    };
    checkLink();
  }, [id]);

  const handleFetch = async (type) => {
    setLoading(true);
    setCurrentType(type);
    try {
      const res = await fetch(
        `${API_ENDPOINT}/api/check-leads-link/${id}?type=${type}`,
        {
          method: "GET",
          ...getHeader(),
        }
      );
      const json = await res.json();

      if ("leadsList" in json && Array.isArray(json.leadsList)) {
        setData(json.leadsList);
        setStartTime(json.startTime);
        setStatus("data");
        exportToExcel(json.leadsList, type);
      } else {
        setStatus("invalid");
        setErrorMessage("Failed to fetch report data.");
      }
    } catch (err) {
      setStatus("invalid");
      setErrorMessage("Error fetching report data. Please try again.");
      console.error("Fetch error:", err);
    }
    setLoading(false);
  };

  const formatTimeInIST = (mongoTimestamp) => {
    if (!mongoTimestamp) return "Unknown time";
    try {
      // Assuming mongoTimestamp is a string like ISODate("...") or direct ISO string; parse as Date
      const date = new Date(mongoTimestamp);
      if (isNaN(date.getTime())) return "Invalid time";
      return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
    } catch (err) {
      console.error("Error formatting time:", err);
      return "Error formatting time";
    }
  };

  const exportToExcel = (exportData, type = currentType) => {
    if (!exportData || exportData.length === 0) return;

    try {
      // Transform data to convert timestamp fields to IST
      const transformedData = exportData.map((row) => {
        const newRow = { ...row };

        // Convert createdOn to IST if it exists
        if (newRow.createdOn) {
          newRow.createdOn = formatTimeInIST(newRow.createdOn);
        }

        // Convert updatedOn to IST if it exists
        if (newRow.updatedOn) {
          newRow.updatedOn = formatTimeInIST(newRow.updatedOn);
        }

        return newRow;
      });

      const ws = XLSX.utils.json_to_sheet(transformedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Leads Report");

      const timestamp = new Date().toISOString().split("T")[0];
      const typeMap = {
        positive: "Positive_Leads",
        negative: "Negative_Responses",
        incoming: "Incoming_Replies",
      };
      const filename = `${
        typeMap[type] || "Leads"
      }_${customerId}_${timestamp}.xlsx`;

      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setErrorMessage("Failed to export to Excel. Please try again.");
    }
  };

  const getButtonConfig = (type) => {
    const configs = {
      positive: {
        icon: <MdThumbUp />,
        label: "Positive Leads",
        color: "success",
        description: "Positive responses and engagements",
      },
      negative: {
        icon: <MdThumbDown />,
        label: "Negative Responses",
        color: "error",
        description: "Opt-out and negative responses",
      },
      incoming: {
        icon: <MdReply />,
        label: "Incoming Replies",
        color: "secondary",
        description: "All incoming text replies",
      },
    };
    return configs[type] || {};
  };

  const renderTable = () => {
    if (!data || data.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No data found for the selected report type.
        </Alert>
      );
    }

    if (isMobile) {
      return (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {data.slice(0, 100).map((row, idx) => (
            <Card key={idx} variant="outlined">
              <CardContent>
                <Stack spacing={1}>
                  {Object.entries(row).map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="caption" color="text.secondary">
                        {key}:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {String(value || "N/A")}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
          {data.length > 100 && (
            <Alert severity="info">
              Showing first 100 records. Export to Excel to see all{" "}
              {data.length} records.
            </Alert>
          )}
        </Stack>
      );
    }

    return (
      <Paper sx={{ width: "100%", overflow: "auto" }}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              {Object.keys(data[0] || {}).map((key) => (
                <TableCell
                  key={key}
                  sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}
                >
                  {key}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              <TableRow key={idx} hover>
                {Object.values(row).map((val, i) => (
                  <TableCell
                    key={i}
                    sx={{ maxWidth: 200, wordBreak: "break-word" }}
                  >
                    {String(val || "N/A")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  if (status === "loading") {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <Stack spacing={3} alignItems="center">
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Checking link validity...
            </Typography>
          </Stack>
        </Box>
      </Container>
    );
  }

  if (status === "expired" || status === "invalid") {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert
          severity={status === "expired" ? "error" : "warning"}
          icon={
            status === "expired" ? (
              <MdError size={24} />
            ) : (
              <MdWarning size={24} />
            )
          }
          sx={{
            "& .MuiAlert-message": {
              display: "flex",
              flexDirection: "column",
              gap: 1,
            },
          }}
        >
          <Typography variant="h6">
            {status === "expired" ? "Link Expired" : "Invalid Link"}
          </Typography>
          <Typography variant="body1">{errorMessage}</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Leads Reports
          </Typography>
          <Tooltip
            title={`Customer ID: ${customerId}`}
            aria-label={`Customer ID: ${customerId}`}
          >
            <Chip
              label={`Customer: ${customerId}`}
              variant="outlined"
              color="primary"
              sx={{
                fontWeight: "medium",
                fontSize: isMobile ? "0.8rem" : "1rem",
              }}
            />
          </Tooltip>
        </Stack>
        <Typography variant="body1" color="text.secondary">
          Generate and export lead reports for customer {customerId}.
        </Typography>
      </Box>

      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMessage("")}
        >
          {errorMessage}
        </Alert>
      )}

      {status === "ok" && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Select Report Type
          </Typography>
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            alignItems={isMobile ? "stretch" : "flex-start"}
          >
            {["positive", "negative", "incoming"].map((type) => {
              const config = getButtonConfig(type);
              return (
                <Tooltip key={type} title={config.description} arrow>
                  <Button
                    variant={currentType === type ? "contained" : "outlined"}
                    color={config.color}
                    onClick={() => handleFetch(type)}
                    disabled={loading}
                    startIcon={config.icon}
                    size={isMobile ? "large" : "medium"}
                    sx={{
                      flex: isMobile ? 1 : undefined,
                      justifyContent: "flex-start",
                      py: 2,
                      textAlign: "left",
                    }}
                    aria-label={`Generate ${config.label} report`}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {config.label}
                      </Typography>
                      {!isMobile && (
                        <Typography variant="caption" display="block">
                          {config.description}
                        </Typography>
                      )}
                    </Box>
                  </Button>
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body1" color="text.secondary">
              Generating {getButtonConfig(currentType).label} Report for{" "}
              {customerId}...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Excel export will start automatically
            </Typography>
          </Stack>
        </Box>
      )}

      {status === "data" && data && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              justifyContent="space-between"
              alignItems={isMobile ? "stretch" : "center"}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  {getButtonConfig(currentType).label} for {customerId}
                </Typography>
                <Chip
                  icon={<MdDownload />}
                  label={`${data.length} records`}
                  variant="outlined"
                  size="small"
                />
                {startTime && (
                  <Chip
                    icon={<MdAccessTime />}
                    label={`From ${formatTimeInIST(startTime)} IST till now`}
                    variant="outlined"
                    size="small"
                    color="info"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>

              <Stack
                direction={isMobile ? "column" : "row"}
                spacing={1}
                width={isMobile ? "100%" : "auto"}
              >
                <Button
                  variant="outlined"
                  startIcon={<MdDownload />}
                  onClick={() => exportToExcel(data)}
                  size={isMobile ? "large" : "medium"}
                  fullWidth={isMobile}
                  aria-label="Export report to Excel"
                >
                  Export Again
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<MdRefresh />}
                  onClick={() => setStatus("ok")}
                  size={isMobile ? "large" : "medium"}
                  fullWidth={isMobile}
                  aria-label="Return to report selection"
                >
                  Back to Reports
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {renderTable()}

          {data.length > 50 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Showing {isMobile ? Math.min(100, data.length) : data.length}{" "}
                records. The Excel file contains all {data.length} records.
              </Typography>
            </Alert>
          )}
        </Box>
      )}
    </Container>
  );
};

export default LeadsReports;
